/*
 * Store locator
 * Reads locations from the TA Mappy: Store Locator & Maps app (its storefront metaobject),
 * the same source Mappy's own widget uses, and renders them as a list + Leaflet map.
 */
(() => {
  if (customElements.get('store-locator')) return;

  // Mappy's app embed prints these globals on every page. Its own script clears
  // BSS_SL_metaobject after it loads, so read them as early as possible.
  const MAPPY = {
    config: window.BSS_SL || {},
    metaobject: window.BSS_SL_metaobject || {},
  };

  const AU_STATES = {
    ACT: 'Australian Capital Territory',
    NSW: 'New South Wales',
    NT: 'Northern Territory',
    QLD: 'Queensland',
    SA: 'South Australia',
    TAS: 'Tasmania',
    VIC: 'Victoria',
    WA: 'Western Australia',
  };

  const ARROW_ICON =
    '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true" focusable="false"><path d="M3.74766 9.00029H14.2477M8.99766 14.2503L14.2477 9.00029L8.99766 3.75029" stroke="currentColor" stroke-width="1.503" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

  const clean = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

  const parseJson = (value, fallback) => {
    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  };

  /* ---------- Data loading ---------- */

  async function fetchMetaobjectLocations(token) {
    const apiVersion = MAPPY.metaobject.apiVersion || '2026-01';
    const maxVersion = Number(MAPPY.config.maxVersion) || 0;
    const fields = '{ fields { key value } }';
    const queries = maxVersion
      ? Array.from({ length: maxVersion }, (_, i) =>
          `v${i + 1}: metaobject(handle: { handle: "bss_sl_metaobject_data", type: "$app:bss_sl_data_v${i + 1}" }) ${fields}`
        )
      : [`v0: metaobject(handle: { handle: "bss_sl_metaobject_data", type: "$app:bss_sl_data" }) ${fields}`];

    const response = await fetch(`/api/${apiVersion}/graphql.json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
      body: JSON.stringify({ query: `{ ${queries.join('\n')} }` }),
    });
    if (!response.ok) throw new Error(`Storefront API responded ${response.status}`);

    const data = (await response.json())?.data || {};
    const locations = [];

    Object.values(data).forEach((metaobject) => {
      (metaobject?.fields || []).forEach(({ key, value }) => {
        if (!value) return;
        if (maxVersion) {
          if (key.startsWith('locations_')) locations.push(...(parseJson(value, {}).data || []));
          return;
        }
        const field = parseJson(value, null);
        if (field?.key?.includes('storeLocators')) locations.push(...parseJson(field.value, []));
      });
    });

    return locations;
  }

  async function fetchApiLocations() {
    const { apiHost, domain } = MAPPY.config;
    const response = await fetch(`${apiHost}/api/embed/storefront/data?shop=${encodeURIComponent(domain)}`);
    if (!response.ok) throw new Error(`Mappy API responded ${response.status}`);
    return (await response.json())?.configData || [];
  }

  // Locations printed by Liquid from Mappy's metaobject (see sections/store-locator.liquid).
  function readInlineLocations(root) {
    const locations = [];
    root.querySelectorAll('[data-mappy-field]').forEach((script) => {
      let field = parseJson(script.textContent, null);
      if (typeof field === 'string') field = parseJson(field, null);
      if (field?.key?.includes('storeLocators')) locations.push(...parseJson(field.value, []));
    });
    return locations;
  }

  async function loadLocations(root, fallbackToken) {
    const inline = readInlineLocations(root);
    if (inline.length) return inline;

    if (Array.isArray(MAPPY.config.configData) && MAPPY.config.configData.length) return MAPPY.config.configData;

    const token = MAPPY.metaobject.storefrontAccessToken || fallbackToken;
    if (token) return fetchMetaobjectLocations(token);
    if (MAPPY.config.apiHost && MAPPY.config.domain) return fetchApiLocations();

    throw new Error('Mappy store data not found. Is the Mappy app embed enabled?');
  }

  /* ---------- Normalising ---------- */

  function findAuState(store) {
    const state = clean(store.state).toUpperCase();
    if (AU_STATES[state]) return state;

    const text = ` ${clean([store.address, store.city, store.state].join(' '))} `.toLowerCase();
    for (const [code, name] of Object.entries(AU_STATES)) {
      if (text.includes(name.toLowerCase()) || new RegExp(`[\\s,]${code.toLowerCase()}[\\s,\\-]`).test(text)) return code;
    }
    return null;
  }

  function findRegion(lat, lng, country) {
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      if (lat <= 0 && (lng >= 110 || lng <= -170)) return 'oceania';
      if (lng <= -30 && lng > -170) return 'americas';
      if (lat >= 34 && lng >= -25 && lng <= 45) return 'europe';
      if (lng > 45 && lat > -11) return 'asia';
    }
    if (/austral|zealand/i.test(country)) return 'oceania';
    return 'other';
  }

  function formatAddress(store) {
    const address = clean(store.address);
    const lower = address.toLowerCase();
    const parts = [address];
    const extras = [
      clean(store.additional_address),
      clean(store.city),
      clean([store.state, store.zipCode].map(clean).filter(Boolean).join(' ')),
      clean(store.country),
    ];
    extras.forEach((part) => {
      if (part && !lower.includes(part.toLowerCase())) parts.push(part);
    });
    return parts.filter(Boolean).join(', ');
  }

  function normalise(store) {
    const lat = parseFloat(store.lat);
    const lng = parseFloat(store.lng);
    const country = clean(store.country);
    const region = findRegion(lat, lng, country);
    const auState = region === 'oceania' ? findAuState(store) : null;
    const area = auState ? AU_STATES[auState] : country && /^austr?ai?l/i.test(country) ? '' : country;

    let web = clean(store.web);
    if (web && !/^https?:\/\//i.test(web)) web = `https://${web}`;

    const normalised = {
      id: String(store.id),
      name: clean(store.storeName),
      address: formatAddress(store),
      phone: clean(store.phone),
      email: clean(store.email),
      web,
      lat,
      lng,
      hasCoords: Number.isFinite(lat) && Number.isFinite(lng),
      region,
      area,
    };
    normalised.search = [normalised.name, normalised.address, area, store.zipCode].join(' ').toLowerCase();
    return normalised;
  }

  const isVisible = (store) => store.active !== 0 && store.status !== 0 && !store.deletedAt && clean(store.storeName);

  /* ---------- Element ---------- */

  class StoreLocator extends HTMLElement {
    connectedCallback() {
      this.strings = parseJson(this.querySelector('[data-strings]')?.textContent, {});
      this.list = this.querySelector('[data-list]');
      this.searchInput = this.querySelector('[data-search]');
      this.areaSelect = this.querySelector('[data-area]');
      this.pills = [...this.querySelectorAll('[data-region]')];
      this.state = { region: 'all', area: '', query: '' };

      this.initMap();
      this.bindEvents();

      loadLocations(this, this.dataset.token)
        .then((locations) => {
          this.stores = locations
            .filter(isVisible)
            .map(normalise)
            .sort((a, b) => a.name.localeCompare(b.name));
          this.createMarkers();
          this.updateCounts();
          this.update();
        })
        .catch((error) => {
          console.error('[store-locator]', error);
          this.showMessage(this.strings.error);
        });
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.map?.remove();
    }

    initMap() {
      const container = this.querySelector('[data-map]');
      if (!window.L || !container) return;

      // Mouse wheel zooms the map while the cursor is over it.
      this.map = L.map(container, { scrollWheelZoom: true, worldCopyJump: true, minZoom: container.offsetWidth < 700 ? 1 : 2 }).setView([-28, 140], 3);
      this.map.zoomControl.setPosition('topright');
      L.tileLayer(this.dataset.tiles, { maxZoom: 19, attribution: this.dataset.attribution }).addTo(this.map);

      this.markerLayer = L.layerGroup().addTo(this.map);
      this.icon = L.icon({ iconUrl: this.dataset.pin, iconSize: [24, 32], iconAnchor: [12, 29], popupAnchor: [0, -26] });

      this.resizeObserver = new ResizeObserver(() => this.map.invalidateSize());
      this.resizeObserver.observe(container);
    }

    bindEvents() {
      this.pills.forEach((pill) =>
        pill.addEventListener('click', () => {
          this.state.region = pill.dataset.region;
          this.state.area = '';
          this.pills.forEach((item) => {
            const active = item === pill;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-pressed', active);
          });
          this.update();
        })
      );

      let timer;
      this.searchInput.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          this.state.query = this.searchInput.value.trim().toLowerCase();
          this.update({ keepAreas: true });
        }, 200);
      });

      this.areaSelect.addEventListener('change', () => {
        this.state.area = this.areaSelect.value;
        this.update({ keepAreas: true });
      });

      this.list.addEventListener('click', (event) => {
        const card = event.target.closest('[data-store-id]');
        if (card && !event.target.closest('a')) this.select(card.dataset.storeId, { fromList: true });
      });

      this.list.addEventListener('keydown', (event) => {
        const card = event.target.closest('[data-store-id]');
        if (card && event.target === card && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          this.select(card.dataset.storeId, { fromList: true });
        }
      });
    }

    createMarkers() {
      if (!this.map) return;
      this.markers = new Map();
      this.stores.forEach((store) => {
        if (!store.hasCoords) return;
        const marker = L.marker([store.lat, store.lng], { icon: this.icon, title: store.name, alt: store.name });
        marker.bindPopup(() => this.renderCard(store, { popup: true }), {
          className: 'store-locator__popup',
          maxWidth: 330,
          autoPanPadding: [24, 24],
        });
        marker.on('click', () => this.select(store.id));
        this.markers.set(store.id, marker);
      });
    }

    updateCounts() {
      const counts = this.stores.reduce((acc, store) => ({ ...acc, [store.region]: (acc[store.region] || 0) + 1 }), {});
      counts.all = this.stores.length;
      this.pills.forEach((pill) => {
        const count = counts[pill.dataset.region] || 0;
        pill.hidden = count === 0;
      });
    }

    updateAreas(stores) {
      const counts = new Map();
      stores.forEach((store) => store.area && counts.set(store.area, (counts.get(store.area) || 0) + 1));
      const defaultOption = this.areaSelect.options[0];
      this.areaSelect.replaceChildren(defaultOption);
      [...counts.keys()]
        .sort((a, b) => a.localeCompare(b))
        .forEach((area) => this.areaSelect.add(new Option(`${area} (${counts.get(area)})`, area)));
      this.areaSelect.value = this.state.area;
      this.areaSelect.disabled = counts.size === 0;
    }

    update({ keepAreas = false } = {}) {
      if (!this.stores) return;
      const { region, area, query } = this.state;
      const inRegion = this.stores.filter((store) => region === 'all' || store.region === region);
      if (!keepAreas) this.updateAreas(inRegion);

      this.filtered = inRegion.filter(
        (store) => (!area || store.area === area) && (!query || store.search.includes(query))
      );

      this.activeId = null;
      this.map?.closePopup();
      this.renderList();
      this.renderMarkers();
    }

    renderList() {
      if (!this.filtered.length) {
        this.showMessage(this.strings.noResults);
        return;
      }
      this.list.innerHTML = this.filtered.map((store) => this.renderCard(store)).join('');
      this.list.scrollTop = 0;
    }

    renderMarkers() {
      if (!this.map || !this.markers) return;
      this.markerLayer.clearLayers();
      const points = [];
      this.filtered.forEach((store) => {
        const marker = this.markers.get(store.id);
        if (!marker) return;
        this.markerLayer.addLayer(marker);
        points.push(marker.getLatLng());
      });
      if (points.length) this.map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 13 });
    }

    renderCard(store, { popup = false } = {}) {
      const s = this.strings;
      const lines = [];
      if (store.address) lines.push(`<p class="store-locator__line">${escapeHtml(store.address)}</p>`);

      const contact = [];
      if (store.phone) {
        contact.push(
          `${escapeHtml(s.phone)} <a href="tel:${escapeHtml(store.phone.replace(/[^\d+]/g, ''))}">${escapeHtml(store.phone)}</a>`
        );
      }
      if (store.email) {
        contact.push(`${escapeHtml(s.email)} <a href="mailto:${escapeHtml(store.email)}">${escapeHtml(store.email)}</a>`);
      }
      if (contact.length) lines.push(`<p class="store-locator__line">${contact.join('<br>')}</p>`);

      const link = store.web
        ? { href: store.web, label: s.viewStore }
        : store.hasCoords
          ? { href: `https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lng}`, label: s.directions }
          : null;

      const linkHtml = link
        ? `<a class="store-locator__link" href="${escapeHtml(link.href)}" target="_blank" rel="noopener">${escapeHtml(link.label)}${ARROW_ICON}</a>`
        : '';

      const attributes = popup
        ? ''
        : ` data-store-id="${escapeHtml(store.id)}" tabindex="0"${store.id === this.activeId ? ' aria-current="true"' : ''}`;

      return `<article class="store-locator__card${!popup && store.id === this.activeId ? ' is-active' : ''}"${attributes}>
        <div class="store-locator__details">
          <h2 class="store-locator__name">${escapeHtml(store.name)}</h2>
          ${lines.join('')}
        </div>
        ${linkHtml}
      </article>`;
    }

    select(id, { fromList = false } = {}) {
      this.activeId = id;
      this.list.querySelectorAll('[data-store-id]').forEach((card) => {
        const active = card.dataset.storeId === id;
        card.classList.toggle('is-active', active);
        if (active) card.setAttribute('aria-current', 'true');
        else card.removeAttribute('aria-current');
        if (active && !fromList) this.list.scrollTo({ top: card.offsetTop, behavior: 'smooth' });
      });

      const marker = this.markers?.get(id);
      if (fromList && marker) {
        this.map.setView(marker.getLatLng(), Math.max(this.map.getZoom(), 13));
        marker.openPopup();
      }
    }

    showMessage(text) {
      this.list.innerHTML = `<p class="store-locator__message">${escapeHtml(text)}</p>`;
    }
  }

  customElements.define('store-locator', StoreLocator);
})();
